/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Carte de disposition légale citée dans l'analyse juridique IA :
 *  - Nature, numéro et date du texte
 *  - Numéro de l'article et extrait officiel de la disposition
 *  - Explication du lien avec le problème
 *  - Statut juridique et badge de vérification de source
 *  - Bouton sécurisé "Consulter le texte officiel →"
 */

import React from 'react';
import { BookOpen, CheckCircle2, AlertCircle, ArrowUpRight, Scale } from 'lucide-react';
import { LegalReferenceItem } from '../../Types/ai';

interface LegalReferenceCardProps {
  reference: LegalReferenceItem;
  onOpenDocument: (documentId?: number) => void;
}

export const LegalReferenceCard: React.FC<LegalReferenceCardProps> = ({
  reference,
  onOpenDocument,
}) => {
  const hasDocument = typeof reference.documentId === 'number' && reference.documentId > 0;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200/90 hover:border-zinc-300 p-5 shadow-xs transition-all space-y-3.5">
      {/* Header : Nature, Référence et Badge de vérification */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 pb-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-zinc-100 text-zinc-800 border border-zinc-200">
            {reference.textNature || 'ACTE'}
          </span>
          <span className="text-xs font-bold text-zinc-900">
            {reference.referenceNumber}
          </span>
          {reference.legalStatus && (
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                reference.legalStatus.toLowerCase().includes('vig')
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-zinc-100 text-zinc-600 border border-zinc-200'
              }`}
            >
              {reference.legalStatus}
            </span>
          )}
        </div>

        {/* Source verification status */}
        <div className="flex items-center gap-1.5 text-[10px] font-semibold">
          {reference.isVerifiedSource ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              <span>Source documentaire vérifiée</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
              <AlertCircle className="w-3 h-3 text-amber-600" />
              <span>Référence à vérifier</span>
            </span>
          )}
        </div>
      </div>

      {/* Official Title & Article number */}
      <div>
        {reference.articleNumber && (
          <div className="text-xs font-extrabold text-primary-750 uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5" />
            <span>{reference.articleNumber}</span>
          </div>
        )}
        <h5 className="text-sm font-bold text-zinc-900 leading-snug">
          {reference.officialTitle}
        </h5>
      </div>

      {/* Extrait exact de la disposition */}
      {reference.articleExcerpt && (
        <div className="bg-zinc-50 rounded-xl p-3.5 border-l-3 border-primary-750 text-xs text-zinc-700 italic leading-relaxed">
          « {reference.articleExcerpt} »
        </div>
      )}

      {/* Explication du lien avec la situation */}
      {reference.relevanceExplanation && (
        <div className="text-xs text-zinc-600 leading-relaxed">
          <strong className="text-zinc-800 font-semibold not-italic">Pertinence juridique : </strong>
          <span>{reference.relevanceExplanation}</span>
        </div>
      )}

      {/* Action Footer : Consultation du texte */}
      {hasDocument && (
        <div className="pt-1 flex items-center justify-end">
          <button
            type="button"
            onClick={() => onOpenDocument(reference.documentId)}
            className="inline-flex items-center gap-1 text-xs font-bold text-primary-750 hover:text-primary-800 hover:underline transition cursor-pointer"
          >
            <span>Consulter le texte officiel</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LegalReferenceCard;
